import { useEffect, useState } from 'react'
import { GripVertical, Loader2, Pencil, Plus, Trash2, X } from 'lucide-react'
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { supabase } from '@/integrations/supabase/client'
import { CATEGORIES, type WishlistCategory } from '@/lib/wishlist'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

interface CategoryManagerDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  userId: string
  categories: WishlistCategory[]
  onChange: (categories: WishlistCategory[]) => void
}

const inputClass =
  'w-full rounded-xl border border-neutral-200 bg-white px-3 py-2.5 text-sm text-neutral-900 outline-none transition focus:border-neutral-900 focus:ring-2 focus:ring-neutral-900/10'

const COLOR_SWATCHES = [
  '#ec4899', '#ef4444', '#f97316', '#eab308',
  '#84cc16', '#22c55e', '#14b8a6', '#06b6d4',
  '#3b82f6', '#6366f1', '#8b5cf6', '#d946ef',
  '#78716c', '#64748b', '#0f172a', '#ffffff',
]

function ColorPicker({
  value,
  onChange,
}: {
  value: string | null
  onChange: (color: string | null) => void
}) {
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {COLOR_SWATCHES.map((c) => (
        <button
          key={c}
          type="button"
          onClick={() => onChange(c === '#ffffff' ? null : c)}
          title={c === '#ffffff' ? 'Colore casuale' : 'Scegli questo colore'}
          className={`h-6 w-6 rounded-full border transition ${
            (value ?? '#ffffff') === c
              ? 'ring-2 ring-offset-2 ring-neutral-900 border-neutral-900'
              : 'border-neutral-200 hover:scale-110'
          }`}
          style={{
            backgroundColor: c,
            backgroundImage: c === '#ffffff' ? 'linear-gradient(135deg, transparent 45%, #ec4899 45%, #3b82f6 55%, transparent 55%)' : undefined,
          }}
        />
      ))}
      <label
        className="relative h-6 w-6 cursor-pointer overflow-hidden rounded-full border border-dashed border-neutral-300 transition hover:scale-110"
        title="Colore personalizzato"
      >
        <span
          className="absolute inset-0"
          style={{ backgroundColor: value && !COLOR_SWATCHES.includes(value) ? value : 'transparent' }}
        />
        <Plus className="absolute inset-0 m-auto h-3 w-3 text-neutral-400" />
        <input
          type="color"
          value={value ?? '#9ca3af'}
          onChange={(e) => onChange(e.target.value)}
          className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
        />
      </label>
    </div>
  )
}

function SortableRow({
  category,
  onRename,
  onDelete,
  onChangeColor,
  busy,
}: {
  category: WishlistCategory
  onRename: (id: string, name: string) => void
  onDelete: (id: string) => void
  onChangeColor: (id: string, color: string | null) => void
  busy: boolean
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: category.id })
  const [editing, setEditing] = useState(false)
  const [value, setValue] = useState(category.name)

  const commit = () => {
    const next = value.trim()
    setEditing(false)
    if (next && next !== category.name) onRename(category.id, next)
    else setValue(category.name)
  }

  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`flex items-center gap-2 rounded-2xl border border-border glass px-3 py-2.5 ${
        isDragging ? 'opacity-70 shadow-lg z-10 relative' : ''
      }`}
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        className="cursor-grab touch-none text-neutral-400 hover:text-neutral-900 active:cursor-grabbing"
        title="Trascina per riordinare"
      >
        <GripVertical className="h-4 w-4" />
      </button>

      {editing ? (
        <input
          autoFocus
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commit()
            if (e.key === 'Escape') {
              setValue(category.name)
              setEditing(false)
            }
          }}
          className={`${inputClass} py-1.5`}
        />
      ) : (
        <span className="flex-1 text-sm font-semibold text-neutral-800">{category.name}</span>
      )}

      {!editing && (
        <>
          <label
            className="relative flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-full border border-neutral-200 transition hover:scale-110"
            title="Cambia colore della label"
          >
            <span
              className="h-4 w-4 rounded-full border border-neutral-200"
              style={{
                backgroundColor: category.color ?? 'transparent',
                backgroundImage: category.color
                  ? undefined
                  : 'linear-gradient(135deg, transparent 45%, #ec4899 45%, #3b82f6 55%, transparent 55%)',
              }}
            />
            <input
              type="color"
              value={category.color ?? '#9ca3af'}
              onChange={(e) => onChangeColor(category.id, e.target.value)}
              className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
            />
          </label>
          <button
            type="button"
            onClick={() => {
              setValue(category.name)
              setEditing(true)
            }}
            title="Rinomina"
            className="flex h-8 w-8 items-center justify-center rounded-full text-neutral-400 transition hover:bg-neutral-100 hover:text-neutral-900"
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={() => onDelete(category.id)}
            disabled={busy}
            title="Elimina"
            className="flex h-8 w-8 items-center justify-center rounded-full text-red-500 transition hover:bg-red-50 disabled:opacity-50"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </>
      )}
    </li>
  )
}

export default function CategoryManagerDialog({
  open,
  onOpenChange,
  userId,
  categories,
  onChange,
}: CategoryManagerDialogProps) {
  const [newName, setNewName] = useState('')
  const [newColor, setNewColor] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }))

  useEffect(() => {
    if (open) {
      setNewName('')
      setNewColor(null)
      setError(null)
    }
  }, [open])

  const persistOrder = async (ordered: WishlistCategory[]) => {
    const withPositions = ordered.map((c, i) => ({ ...c, position: i }))
    onChange(withPositions)
    await Promise.all(
      withPositions.map((c) =>
        supabase.from('wishlist_categories').update({ position: c.position }).eq('id', c.id)
      )
    )
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const oldIndex = categories.findIndex((c) => c.id === active.id)
    const newIndex = categories.findIndex((c) => c.id === over.id)
    if (oldIndex < 0 || newIndex < 0) return
    void persistOrder(arrayMove(categories, oldIndex, newIndex))
  }

  const addCategory = async () => {
    const name = newName.trim()
    if (!name || busy) return
    if (categories.some((c) => c.name.toLowerCase() === name.toLowerCase())) {
      setError('Esiste già una categoria con questo nome')
      return
    }
    setBusy(true)
    setError(null)
    const position = categories.length
    const { data, error: err } = await supabase
      .from('wishlist_categories')
      .insert({ user_id: userId, name, position, color: newColor })
      .select()
      .single()
    setBusy(false)
    if (err) {
      setError(err.message)
      return
    }
    onChange([...categories, data as unknown as WishlistCategory])
    setNewName('')
    setNewColor(null)
  }

  const renameCategory = async (id: string, name: string) => {
    if (categories.some((c) => c.id !== id && c.name.toLowerCase() === name.toLowerCase())) {
      setError('Esiste già una categoria con questo nome')
      return
    }
    setError(null)
    const prev = categories
    onChange(categories.map((c) => (c.id === id ? { ...c, name } : c)))
    const { error: err } = await supabase
      .from('wishlist_categories')
      .update({ name })
      .eq('id', id)
    if (err) {
      setError(err.message)
      onChange(prev)
      return
    }
    // Gli oggetti con la vecchia categoria seguono il nuovo nome.
    const old = prev.find((c) => c.id === id)
    if (old) {
      await supabase
        .from('wishlist_items')
        .update({ category: name })
        .eq('user_id', userId)
        .eq('category', old.name)
    }
  }

  const changeColor = async (id: string, color: string | null) => {
    onChange(categories.map((c) => (c.id === id ? { ...c, color } : c)))
    const { error: err } = await supabase
      .from('wishlist_categories')
      .update({ color })
      .eq('id', id)
    if (err) setError(err.message)
  }

  const deleteCategory = async (id: string) => {
    setBusy(true)
    setError(null)
    const removed = categories.find((c) => c.id === id)
    const { error: err } = await supabase.from('wishlist_categories').delete().eq('id', id)
    if (err) {
      setError(err.message)
      setBusy(false)
      return
    }
    // Gli oggetti di quella categoria tornano in "Altro".
    if (removed) {
      await supabase
        .from('wishlist_items')
        .update({ category: 'Accessori' })
        .eq('user_id', userId)
        .eq('category', removed.name)
    }
    setBusy(false)
    onChange(categories.filter((c) => c.id !== id).map((c, i) => ({ ...c, position: i })))
  }

  const suggestions = CATEGORIES.filter(
    (c) => !categories.some((existing) => existing.name === c)
  )

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-3xl max-w-md max-h-[90vh] overflow-y-auto glass-strong">
        <DialogHeader>
          <DialogTitle className="text-xl font-extrabold text-neutral-900">
            Le tue categorie
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-neutral-500">
          Trascina per riordinare, rinomina o elimina. L'ordine si riflette sulla tua wishlist pubblica.
        </p>

        <div className="mt-4 flex gap-2">
          <input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && addCategory()}
            placeholder="Nuova categoria…"
            className={inputClass}
          />
          <button
            type="button"
            onClick={addCategory}
            disabled={busy || !newName.trim()}
            className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-neutral-900 px-4 py-2 text-sm font-semibold text-white transition hover:bg-neutral-800 disabled:opacity-50"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            Aggiungi
          </button>
        </div>

        <div className="mt-3">
          <p className="text-xs font-semibold text-neutral-500">
            Colore della label {newColor ? '' : '(casuale se non selezioni)'}
          </p>
          <div className="mt-1.5">
            <ColorPicker value={newColor} onChange={setNewColor} />
          </div>
        </div>

        {suggestions.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {suggestions.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setNewName(s)}
                className="rounded-full bg-neutral-100 px-3 py-1 text-xs font-semibold text-neutral-900 transition hover:bg-neutral-100"
              >
                + {s}
              </button>
            ))}
          </div>
        )}

        {error && <p className="mt-3 text-xs text-neutral-900">{error}</p>}

        {categories.length === 0 ? (
          <p className="mt-6 rounded-2xl bg-neutral-100 px-4 py-6 text-center text-sm text-slate-500">
            Nessuna categoria personalizzata: per ora vengono usate quelle dei tuoi oggetti. Creane una qui sopra!
          </p>
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={categories.map((c) => c.id)} strategy={verticalListSortingStrategy}>
              <ul className="mt-4 space-y-2">
                {categories.map((category) => (
                  <SortableRow
                    key={category.id}
                    category={category}
                    onRename={renameCategory}
                    onDelete={deleteCategory}
                    onChangeColor={changeColor}
                    busy={busy}
                  />
                ))}
              </ul>
            </SortableContext>
          </DndContext>
        )}

        <button
          type="button"
          onClick={() => onOpenChange(false)}
          className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full border border-slate-200 px-6 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
        >
          <X className="h-4 w-4" />
          Chiudi
        </button>
      </DialogContent>
    </Dialog>
  )
}
